import React, { useMemo, useState } from "react";
import { AlertTriangle, ArrowRight, Plus, Loader2, X } from "lucide-react";
import api from "@/lib/api";
import { useWorkspace } from "@/lib/workspace-context";

/**
 * Case-insensitive fuzzy match - mirrors backend's `_fuzzy_match` so the
 * inline banner and the 409 dialog use identical logic.
 */
function fuzzyMatch(a, b) {
  if (!a || !b) return true;
  const aa = String(a).trim().toLowerCase();
  const bb = String(b).trim().toLowerCase();
  if (!aa || !bb) return true;
  return aa === bb || aa.includes(bb) || bb.includes(aa);
}

/**
 * Detects an anticipated workspace mismatch on the client BEFORE submit.
 * Returns { mismatch: boolean, suggested: workspace|null, current: workspace|null }
 * so the caller can render an inline warning next to their form.
 */
export function useWorkspaceMismatch({ role, company }) {
  const { workspaces, active } = useWorkspace();
  return useMemo(() => {
    if (!active) return { mismatch: false, suggested: null, current: null };
    const wsRole = (active.role || "").trim();
    const wsCompany = (active.company || "").trim();
    // Nothing to compare if the workspace has no metadata, or if the user
    // hasn't typed a role/company yet.
    if (!wsRole && !wsCompany) return { mismatch: false, suggested: null, current: active };
    if (!role && !company) return { mismatch: false, suggested: null, current: active };
    const roleOk = wsRole && role ? fuzzyMatch(role, wsRole) : true;
    const companyOk = wsCompany && company ? fuzzyMatch(company, wsCompany) : true;
    if (roleOk && companyOk) return { mismatch: false, suggested: null, current: active };
    const suggested = workspaces
      .filter((w) => w.workspace_id !== active.workspace_id && w.status !== "archived")
      .find((w) => {
        const r = fuzzyMatch(role, w.role);
        const c = fuzzyMatch(company, w.company);
        return (role && w.role && r) && (!company || !w.company || c);
      }) || null;
    return { mismatch: true, suggested, current: active };
  }, [active, workspaces, role, company]);
}

/**
 * Inline warning banner rendered above a form when the active workspace's
 * role/company differ from what the user is currently typing. Tapping the
 * suggested workspace switches immediately; when no workspace matches, a
 * one-click "Create workspace" flow appears so the user can silo new
 * work without leaving the form. Tapping "Dismiss" hides the banner for
 * the session.
 */
export function WorkspaceMismatchBanner({ role, company, jd, onDismiss, dismissed, onCreated }) {
  const { mismatch, suggested, current } = useWorkspaceMismatch({ role, company });
  const { setActive, refresh, premium } = useWorkspace();
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState(null);
  if (!mismatch || dismissed) return null;
  const currentLabel = [current?.role, current?.company].filter(Boolean).join(" @ ") || current?.name;
  const providedLabel = [role, company].filter(Boolean).join(" @ ");

  const createWorkspace = async () => {
    if (creating) return;
    setCreating(true);
    setCreateErr(null);
    try {
      const { data } = await api.post("/workspaces", {
        name: providedLabel || role || "New position",
        role: role || null,
        company: company || null,
        job_description: jd || null,
      });
      // Add to context + activate.
      if (refresh) await refresh();
      if (data?.workspace_id) setActive(data.workspace_id);
      if (onCreated) onCreated(data);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 402) {
        setCreateErr("Free plan supports one workspace. Upgrade to add more.");
      } else if (typeof detail === "string") {
        setCreateErr(detail);
      } else {
        setCreateErr("Couldn't create the workspace. Please try again.");
      }
    } finally {
      setCreating(false);
    }
  };

  return (
    <div
      data-testid="workspace-mismatch-banner"
      className="mb-4 rounded-xl border border-amber-400/40 bg-amber-400/[0.06] px-4 py-3 flex items-start gap-3"
    >
      <AlertTriangle className="h-4 w-4 text-amber-300 mt-0.5 shrink-0" />
      <div className="flex-1 text-[13px] leading-relaxed text-amber-100/90">
        <div className="font-semibold text-amber-200">
          Different position detected
        </div>
        <div className="text-amber-100/80 mt-0.5">
          You&apos;re editing <span className="font-medium text-amber-100">{providedLabel || "this job"}</span> while your active workspace is{" "}
          <span className="font-medium text-amber-100">{currentLabel}</span>. Your work will be saved to the active workspace unless you switch.
        </div>
        {createErr && (
          <div
            data-testid="workspace-mismatch-create-error"
            className="mt-2 text-[12px] text-rose-300"
          >
            {createErr}
          </div>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {suggested ? (
            <button
              type="button"
              onClick={() => setActive(suggested.workspace_id)}
              data-testid="workspace-mismatch-switch"
              className="inline-flex items-center gap-1.5 rounded-md bg-amber-400/20 border border-amber-400/50 px-2.5 py-1 text-[12px] font-semibold text-amber-100 hover:bg-amber-400/30 transition-colors"
            >
              Switch to {[suggested.role, suggested.company].filter(Boolean).join(" @ ") || suggested.name}
              <ArrowRight className="h-3 w-3" />
            </button>
          ) : (
            <button
              type="button"
              onClick={createWorkspace}
              disabled={creating || (!role && !company)}
              data-testid="workspace-mismatch-create"
              className="inline-flex items-center gap-1.5 rounded-md bg-amber-400 text-zinc-950 border border-amber-400 px-2.5 py-1 text-[12px] font-semibold hover:bg-amber-300 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {creating ? (
                <><Loader2 className="h-3 w-3 animate-spin" /> Creating…</>
              ) : (
                <><Plus className="h-3 w-3" /> Create workspace for {providedLabel || "this role"}</>
              )}
            </button>
          )}
          <button
            type="button"
            onClick={onDismiss}
            data-testid="workspace-mismatch-dismiss"
            className="inline-flex items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-900/40 px-2.5 py-1 text-[12px] font-medium text-zinc-300 hover:bg-zinc-800 transition-colors"
          >
            <X className="h-3 w-3" /> Dismiss
          </button>
        </div>
        {!suggested && !premium && !createErr && (
          <div className="mt-1 text-[11px] text-amber-100/60">
            Free plan is limited to one workspace. Upgrade to keep multiple positions separate.
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Modal that appears when the backend returns HTTP 409 workspace_mismatch.
 * Offers up to four exits: switch to the suggested workspace and retry,
 * create a brand-new workspace for the pasted role/company and retry,
 * continue in the current workspace (retry with
 * `confirm_workspace_mismatch=true`), or cancel.
 */
export function WorkspaceMismatchDialog({ detail, jd, onSwitch, onContinue, onCancel, onCreated }) {
  const { setActive, refresh, premium } = useWorkspace();
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState(null);
  if (!detail) return null;
  const wsLabel = [detail.workspace_role, detail.workspace_company].filter(Boolean).join(" @ ") || detail.workspace_name;
  const providedLabel = [detail.provided_role, detail.provided_company].filter(Boolean).join(" @ ");
  const suggestedLabel = [detail.suggested_workspace_role, detail.suggested_workspace_company].filter(Boolean).join(" @ ")
    || detail.suggested_workspace_name;

  const createWorkspace = async () => {
    if (creating) return;
    setCreating(true);
    setCreateErr(null);
    try {
      const { data } = await api.post("/workspaces", {
        name: providedLabel || detail.provided_role || "New position",
        role: detail.provided_role || null,
        company: detail.provided_company || null,
        job_description: jd || null,
      });
      if (refresh) await refresh();
      if (data?.workspace_id) setActive(data.workspace_id);
      if (onCreated) onCreated(data);
    } catch (e) {
      const status = e?.response?.status;
      const d = e?.response?.data?.detail;
      if (status === 402) {
        setCreateErr("Free plan supports one workspace. Upgrade to add more.");
      } else if (typeof d === "string") {
        setCreateErr(d);
      } else {
        setCreateErr("Couldn't create the workspace. Please try again.");
      }
    } finally {
      setCreating(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="workspace-mismatch-dialog"
    >
      <div className="w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-950 shadow-2xl overflow-hidden">
        <div className="p-5 border-b border-zinc-800 flex items-start gap-3">
          <div className="h-9 w-9 shrink-0 rounded-lg bg-amber-400/15 border border-amber-400/40 flex items-center justify-center">
            <AlertTriangle className="h-4 w-4 text-amber-300" />
          </div>
          <div className="flex-1">
            <div className="text-[15px] font-semibold text-zinc-50">Different position?</div>
            <div className="text-[13px] text-zinc-400 mt-0.5">
              Confirm where this work should be saved.
            </div>
          </div>
        </div>
        <div className="p-5 space-y-3 text-[13px] leading-relaxed text-zinc-300">
          <p>
            You&apos;re working on{" "}
            <span className="font-semibold text-amber-200">{providedLabel || "this job"}</span>, but your active workspace is{" "}
            <span className="font-semibold text-zinc-100">{wsLabel}</span>.
          </p>
          <p className="text-zinc-400">
            Saving to the wrong workspace pollutes its Journey and Dashboard. Pick where this belongs before we proceed.
          </p>
          {createErr && (
            <p
              data-testid="workspace-mismatch-dialog-create-error"
              className="text-[12px] text-rose-300"
            >
              {createErr}
            </p>
          )}
        </div>
        <div className="p-4 border-t border-zinc-800 space-y-2">
          {detail.suggested_workspace_id ? (
            <button
              type="button"
              onClick={onSwitch}
              data-testid="workspace-mismatch-dialog-switch"
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-amber-400 text-zinc-950 hover:bg-amber-300 px-4 py-2.5 text-[13px] font-semibold transition-colors"
            >
              Switch to {suggestedLabel} and retry
              <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={createWorkspace}
              disabled={creating}
              data-testid="workspace-mismatch-dialog-create"
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-amber-400 text-zinc-950 hover:bg-amber-300 disabled:opacity-60 disabled:cursor-not-allowed px-4 py-2.5 text-[13px] font-semibold transition-colors"
            >
              {creating ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Creating workspace…</>
              ) : (
                <><Plus className="h-4 w-4" /> Create workspace for {providedLabel || "this role"}</>
              )}
            </button>
          )}
          <button
            type="button"
            onClick={onContinue}
            data-testid="workspace-mismatch-dialog-continue"
            className="w-full rounded-lg border border-zinc-700 bg-zinc-900 hover:bg-zinc-800 px-4 py-2.5 text-[13px] font-medium text-zinc-100 transition-colors"
          >
            Save to {wsLabel} anyway
          </button>
          {!detail.suggested_workspace_id && !premium && (
            <div className="text-[11px] text-zinc-500 text-center pt-1">
              Creating a workspace requires Career Pro on your account.
            </div>
          )}
          <button
            type="button"
            onClick={onCancel}
            data-testid="workspace-mismatch-dialog-cancel"
            className="w-full text-[13px] font-medium text-zinc-400 hover:text-zinc-200 py-1.5 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
